/**
 * App console logger driven by B2C_CASHFREE_ENV (B2C build / Supabase fallback).
 * - TEST (any case): show all logs (debug, info, warn, error).
 * - PROD (any case): show only warn and error.
 *
 * Override: set VITE_SHOW_CONSOLE_LOGS=true in .env to force all logs on
 * regardless of B2C_CASHFREE_ENV. Useful for debugging in production builds.
 *
 * B2C_CASHFREE_ENV is injected at build time via Vite define; set in .env or Netlify.
 * Restart the dev server after changing any .env value.
 */

declare const __B2C_CASHFREE_ENV__: string;
declare const __CASHFREE_ENV__: string;

const raw =
  typeof __B2C_CASHFREE_ENV__ !== 'undefined' && __B2C_CASHFREE_ENV__
    ? __B2C_CASHFREE_ENV__
    : typeof __CASHFREE_ENV__ !== 'undefined'
      ? __CASHFREE_ENV__
      : typeof import.meta !== 'undefined' && import.meta.env
        ? (import.meta.env.B2C_CASHFREE_ENV ?? import.meta.env.CASHFREE_ENV ?? import.meta.env.VITE_APP_ENV ?? '')
        : '';

// Normalize: trim, lowercase, strip non-alpha (e.g. \r on Windows)
const env = String(raw).trim().toLowerCase().replace(/\s+/g, '');

/** CASHFREE_ENV value (TEST, PROD, etc.) for use by payment config and others. */
export const cashfreeEnv = raw;

/** VITE_SHOW_CONSOLE_LOGS=true forces all logs on regardless of CASHFREE_ENV. */
const forceShowLogs =
  typeof import.meta !== 'undefined' &&
  import.meta.env?.VITE_SHOW_CONSOLE_LOGS === 'true';

/** True when we should restrict console to warn/error only (prod). Default PROD when missing/empty. */
export const isProdConsole =
  !forceShowLogs &&
  (env === '' || env === 'prod' || env === 'production' || env.startsWith('prod'));

/** Show all levels (debug, info, warn, error) when explicitly TEST or VITE_SHOW_CONSOLE_LOGS=true. */
export const isTestConsole = !isProdConsole;

const noop = () => {};

function doLog(
  method: 'debug' | 'info' | 'warn' | 'error',
  allow: boolean,
  ...args: unknown[]
) {
  if (!allow) return;
  const fn = console[method];
  if (typeof fn === 'function') fn.apply(console, args);
}

export const appLogger = {
  debug: (...args: unknown[]) => doLog('debug', isTestConsole, ...args),
  info: (...args: unknown[]) => doLog('info', isTestConsole, ...args),
  warn: (...args: unknown[]) => doLog('warn', true, ...args),
  error: (...args: unknown[]) => doLog('error', true, ...args),
};

let consolePatched = false;

/**
 * Call once at app bootstrap (e.g. main.tsx).
 * When CASHFREE_ENV is PROD, replaces console.log and console.info with no-ops
 * so existing console.log/info across the app are suppressed.
 */
export function applyConsoleFilter(): void {
  if (consolePatched || !isProdConsole) return;
  consolePatched = true;
  const origLog = console.log;
  const origInfo = console.info;
  console.log = noop;
  console.info = noop;
  // Preserve ability to restore for debugging if needed
  if (typeof window !== 'undefined') {
    (window as unknown as { __restoreConsole?: () => void }).__restoreConsole = () => {
      console.log = origLog;
      console.info = origInfo;
    };
  }
}
